import { brl, pct, num } from "@/lib/format";
import { fileUrl } from "@/lib/api";
import { esc, openPrintable } from "@/lib/pdf";
import { unitLabel } from "@/lib/recipeCalc";

const CSS = `
body{font-family:-apple-system,Helvetica,Arial,sans-serif;color:#1C1C1A;padding:24px;margin:0}
.sheet{page-break-after:always;padding-bottom:12px}.sheet:last-child{page-break-after:auto}
h1{font-size:24px;margin:0}h2{font-size:15px;margin:20px 0 8px;color:#4E6E5D;text-transform:uppercase;letter-spacing:.04em}
.muted{color:#8A8A85;font-size:12px}.tags{display:flex;gap:8px;flex-wrap:wrap;margin-top:8px}
.tag{background:#EEF3EF;color:#4E6E5D;border-radius:999px;padding:3px 10px;font-size:12px;font-weight:600}
.top{display:flex;gap:20px;align-items:flex-start;margin-top:16px}.top img{width:260px;height:195px;object-fit:cover;border-radius:12px}
.kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;flex:1}.kpi{background:#F4F4F2;border-radius:10px;padding:10px;font-size:11px;color:#8A8A85}
.kpi b{display:block;font-size:16px;color:#1C1C1A;margin-top:3px}
table{width:100%;border-collapse:collapse;font-size:12px}td,th{padding:7px 4px;border-bottom:1px solid #E5E5E2;text-align:left}
th{color:#8A8A85;font-weight:600}.r{text-align:right}tfoot td{font-weight:700;border-bottom:none}
ol{padding-left:20px;margin:0}ol li{margin-bottom:8px;line-height:1.5;font-size:13px}
.kitchen td{font-size:15px;padding:9px 4px}.kitchen ol li{font-size:15px}
.plating{display:flex;gap:16px;align-items:flex-start}.plating img{width:220px;height:165px;object-fit:cover;border-radius:10px}
.foot{margin-top:18px}`;

const img = (path, cls = "") => (path ? `<img class="${cls}" src="${esc(fileUrl(path))}" alt=""/>` : "");

function ingredientsTable(r, withCosts) {
  const rows = r.ingredients.map((i) => {
    const name = `${esc(i.product_name)}${i.kind === "recipe" ? ' <small class="muted">(preparo base)</small>' : ""}`;
    const u = esc(unitLabel(i.unit));
    if (!withCosts) return `<tr><td>${name}</td><td class="r"><b>${num(i.qty_net)} ${u}</b></td><td class="r">${num(i.qty_gross)} ${u}</td></tr>`;
    return `<tr><td>${name}</td><td class="r">${num(i.qty_gross)} ${u}</td><td class="r">${num(i.loss_pct || 0, 1)}%</td><td class="r">${num(i.qty_net)} ${u}</td><td class="r">${i.unit_cost === null || i.unit_cost === undefined ? "—" : `${brl(i.unit_cost)}/${esc(unitLabel(i.product_unit))}`}</td><td class="r">${brl(i.cost)}</td></tr>`;
  }).join("") || `<tr><td colspan="6" class="muted">Sem ingredientes</td></tr>`;
  const head = withCosts
    ? `<tr><th>Ingrediente</th><th class="r">Qtd. bruta</th><th class="r">Perda</th><th class="r">Qtd. líquida</th><th class="r">Custo unit.</th><th class="r">Custo</th></tr>`
    : `<tr><th>Ingrediente</th><th class="r">Qtd. líquida</th><th class="r">Qtd. bruta</th></tr>`;
  const foot = withCosts ? `<tfoot><tr><td colspan="5">Custo total da receita</td><td class="r">${brl(r.total_cost)}</td></tr></tfoot>` : "";
  return `<table>${head}${rows}${foot}</table>`;
}

function kpis(r) {
  const k = (l, v) => `<div class="kpi">${l}<b>${v}</b></div>`;
  return `<div class="kpis">${k("Custo por porção", brl(r.cost_per_portion))}${k("Preço de venda", r.sale_price ? brl(r.sale_price) : "—")}${k("CMV teórico", pct(r.cmv_pct))}${k("Margem", r.margin === null || r.margin === undefined ? "—" : `${brl(r.margin)} (${pct(r.margin_pct)})`)}${k("Meta de CMV", pct(r.cmv_target, 1))}${k("Preço sugerido", r.suggested_price ? brl(r.suggested_price) : "—")}${k("Custo total", brl(r.total_cost))}${r.yield_qty ? k(`Custo por ${esc(r.yield_unit)}`, brl(r.yield_unit_cost)) : k("Porções", num(r.yield_portions))}</div>`;
}

function sheet(r, withCosts) {
  const tags = [
    r.is_base && "Preparo base",
    r.prep_time_min && `${r.prep_time_min} min`,
    `Rende ${num(r.yield_portions)} porção(ões)`,
    r.yield_qty && `${num(r.yield_qty)} ${esc(r.yield_unit)}`,
  ].filter(Boolean).map((t) => `<span class="tag">${t}</span>`).join("");
  const steps = (r.steps || []).map((s) => `<li>${esc(s)}</li>`).join("") || `<li class="muted">Sem etapas cadastradas</li>`;
  const plating = r.plating_notes || r.plating_photo_path
    ? `<h2>Empratamento</h2><div class="plating">${img(r.plating_photo_path)}<p style="flex:1;margin:0;white-space:pre-wrap;font-size:14px">${esc(r.plating_notes)}</p></div>` : "";
  return `<section class="sheet ${withCosts ? "" : "kitchen"}">
    <h1>${esc(r.name)}</h1><div class="muted">${esc(r.menu_category || "Sem categoria")}${r.sales_code ? ` · Cód. PDV ${esc(r.sales_code)}` : ""}</div>
    <div class="tags">${tags}</div>
    <div class="top">${img(r.photo_path)}${withCosts ? kpis(r) : ""}</div>
    <h2>Ingredientes</h2>${ingredientsTable(r, withCosts)}
    ${withCosts ? "" : '<p class="muted">Quantidades líquidas já descontam as perdas de pré-preparo.</p>'}
    <h2>Modo de preparo</h2><ol>${steps}</ol>${plating}
  </section>`;
}

export function exportRecipesPdf(recipes, { withCosts, unitName, appName }) {
  const title = recipes.length === 1 ? `Ficha técnica · ${recipes[0].name}` : `Fichas técnicas (${recipes.length})`;
  const html = `<html><head><meta charset="utf-8"/><title>${esc(title)}</title><style>${CSS}</style></head><body>
  ${recipes.map((r) => sheet(r, withCosts)).join("")}
  <p class="muted foot">${esc(appName || "")} · ${esc(unitName || "")} · ${withCosts ? "Versão completa com custos" : "Versão cozinha"} · Gerado em ${new Date().toLocaleString("pt-BR")}</p>
  <script>window.onload=function(){setTimeout(function(){window.print()},400)}</script>
  </body></html>`;
  return openPrintable(html);
}
